import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Menu } from '@bloomreach/spa-sdk';
import {
  AnalyticsService,
  BreadcrumbItem,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { BaseProductComponent } from '@products/base-product-component';
import { FavoritesService } from '@products/services/favorites.service';
import {
  AsyncContentManagerService,
  ProfileService,
  WindowScrollService,
} from '@services';
import {
  BrBreadcrumb,
  ConfigurationId,
  EDSPaginationPageChangedEvent,
  PaginationActionEnum,
  PPSSState,
  SwitchState,
  Tabs,
} from '@types';
import { ANALTICS_PAGINATION_EVENT } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FundListingComponentConfig } from '../fund-listing-component.config';
import { FundListingDisplayComponent } from '../fund-listing-display-component.interface';
import { AgGridColFactoryService } from '../services/ag-grid-col-factory.service';
import { AgGridHelperService } from '../services/ag-grid-helper.service';
import { PpssDownloadService } from '../services/ppss-download.service';
import { PpssService } from '../services/ppss.service';
import { PpssServiceFactory } from '../services/ppss.service.factory';
import { ProductFilterStateManagerService } from '../services/product-filter-state-manager.service';

const logger = Logger.getLogger('PpssComponent');

interface ItemsPerPageOption {
  label?: string;
  value: number;
}

@Component({
  selector: 'ft-ppss',
  templateUrl: './ppss.component.html',
  styleUrls: ['./ppss.component.scss'],
  providers: [
    AgGridHelperService,
    AgGridColFactoryService,
    ProductFilterStateManagerService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpssComponent
  extends BaseProductComponent
  implements FundListingDisplayComponent, OnInit, OnDestroy {
  public id: string; // this is the BR defined component id
  public componentConfiguration: FundListingComponentConfig;
  public menu: Menu;
  private dataSubscription: Subscription;
  public currentTable: string;
  public tabs: Tabs;
  public pageSize: any;
  public totalNumberOfData: number;
  public numberofFilteredData: number;
  private itemsPerPageOption: ItemsPerPageOption[] = [];
  public itemsPerPageBottomOption: ItemsPerPageOption[] = [];
  public placementTop: string;
  public breadcrumbs: BreadcrumbItem[];
  public selected: string;
  public canShowResults: boolean;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public numberOfPages$: Observable<number>;
  public isHandheld$: Observable<boolean>;

  public showTabContent: any = {};
  private ppssService: PpssService;
  public yieldTabName = '';

  public showFavoritesOnlySwitch: boolean; // To show favorite only switch button on ppss page.
  public favoriteOnlySwitchState: SwitchState; // Set default favorite only state - off/on.
  public showDownloadExcelButton: boolean;

  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;

  public performanceStatusCaveat: string;
  public hidePerformanceTabHeading: boolean;

  constructor(
    private ppssServiceFactory: PpssServiceFactory,
    private changeDetector: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private agGridHelperService: AgGridHelperService,
    private analytics: AnalyticsService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private profileService: ProfileService,
    private acm: AsyncContentManagerService,
    private favoriteService: FavoritesService,
    private scrollService: WindowScrollService,
    private ppssDownloadService: PpssDownloadService
  ) {
    super();
    this.ppssService = this.ppssServiceFactory.getPpssService();
    this.activatedRoute.fragment.subscribe((fragment: string) => {
      if (fragment === 'ratings') {
        this.onSelected('RATINGS');
        this.currentTable = 'RATINGS';
      }
    });

    this.yieldTabName =
      !this.ppssService.isSiteIntl &&
      this.ppssService.getProductType() === ConfigurationId.CEF
        ? 'products.yields-tab-cef'
        : 'products.yields-tab';
    this.activatedRoute.fragment.subscribe((fragment: string) => {
      if (fragment === 'ratings') {
        this.onSelected('RATINGS');
      }
    });
  }

  public ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.numberOfPages$ = this.ppssService
      .getNumberOfPages$()
      .pipe(takeUntil(this.unsubscribe$));
    this.createDropDownRowNoItems();
    this.handlePagination();

    if (
      this.ppssService.getProductType() === ConfigurationId.VIP &&
      !this.profileService.isLoggedIn()
    ) {
      this.hidePerformanceTabHeading = true;
    }
  }

  /**
   * Item-per-page data.
   */
  private createDropDownRowNoItems(): void {
    this.itemsPerPageOption = [25, 50, 75, 100].map((rows) => ({
      value: rows,
      label: String(rows),
    }));
    this.itemsPerPageBottomOption = [25, 50, 75, 100].map((rows) => ({
      value: rows,
      label: String(rows),
    }));
  }

  /**
   * Handling Pagination.
   */
  private handlePagination(): void {
    this.ppssService
      .getNumberOfPages$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.paginationComponent?.updatePagination(data);
        this.pageSize = this.itemsPerPageOption.find(
          (options) => Number(options.label) === this.ppssService.getPageSize()
        );
        this.changeDetector.detectChanges();
      });
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  public onPageChanged(event: EDSPaginationPageChangedEvent): void {
    logger.debug('onPageChanged()', event);
    if (event.action !== 'load') {
      this.scrollService.scrollToAnchor('ppssScrollPosition');
      this.ppssService.goToPage(event.page);
      this.analytics.trackEvent({
        event: ANALTICS_PAGINATION_EVENT,
        name: this.titleService.getTitle(),
        action:
          event.action === PaginationActionEnum.activePage
            ? event.page.toString()
            : event.action,
      });
      // WDE-468 this code is deactivated to stop scrolling when filters are changed.
      // The code was originally added by NGC-12318, but it's not clear from the jira
      // exactly what the original issue was.
      // Scrolling still happens on pagination events (i.e. not 'load') which seems to
      // have been the main requirement
      // } else {
      //   // Wait for content to be present before scrolling on load
      //   timer(50)
      //     .pipe(takeUntil(this.unsubscribe$))
      //     .subscribe(() => {
      //       this.scroller.scrollToAnchor('ppssScrollPosition');
      //     });
    }
  }

  /**
   * Item per page on change event
   */
  public onDropdownChange(numberOfRowsEvent): void {
    this.ppssService.updateNumberOfRows(Number(numberOfRowsEvent.value));
  }

  /**
   * Initial method called by fund listing entry component.
   * Invokes mf service and fetch the feed.
   */
  public populate(
    componentConfiguration: FundListingComponentConfig,
    menu: Menu,
    breadcrumbs: BrBreadcrumb[]
  ): void {
    this.favoriteService
      ?.hasFavorites$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((hasFavorites) => {
        if (hasFavorites) {
          this.favoriteService.loadFavorites();
        }
      });

    this.placementTop = `PPSS${componentConfiguration.configurationName}Top`;
    this.placementTop = this.placementTop.replace(/-|\s/g, '');
    this.componentConfiguration = componentConfiguration;
    this.menu = menu;
    this.canShowResults = true;
    if (this.ppssService) {
      this.tabs = this.ppssService.getState().config.tabs;
      this.agGridHelperService.configurationName = this.ppssService.getProductType();
      this.dataSubscription = this.ppssService.subscribe((newState) =>
        this.mapState(newState)
      );
      this.ppssService.populate();
    }
    // Missing breadcrumbs on page break code execution
    this.breadcrumbs = breadcrumbs?.map((bc) => ({
      content: bc.title,
      href: bc.url,
    }));
  }

  public onSelected(selected: string): void {
    this.selected = selected;
    this.showTabContent[selected] = true;
    this.ppssService.updateCurrentTab(this.selected);
  }

  /**
   * Called when the state changes.
   */
  private mapState(newState: PPSSState): void {
    const hasLoadedFullFeed = newState && newState.hasLoadedFullFeed;

    if (!hasLoadedFullFeed) {
      this.isPopulated = false;
      return;
    }

    this.currentTable = newState.config.currentTable;

    // if we get to here without erroring, data has been populated successfully
    this.isPopulated = true;

    this.performanceStatusCaveat = newState.config.performanceStatusCaveat;
    this.acm.update(this.id, true);

    this.numberofFilteredData = newState.data.filteredTableRows.length;
    this.totalNumberOfData = newState.data.tableRows.length;

    // Assign favorites only state.
    this.showFavoritesOnlySwitch = newState.config.showFavoritesOnlySwitch;
    this.favoriteOnlySwitchState = newState.config.favoriteOnlySwitchState;

    this.showDownloadExcelButton = newState.config.showDownloadDataButton;
    this.changeDetector.detectChanges();
  }

  public downloadExcelHandler(): void {
    this.ppssDownloadService.downloadAsExcel(
      this.ppssService.getState(),
      this.ppssService.getProductType()
    );
  }

  // ********** WDE-1260: feature added **********
  /**
   * Handle the favorites only switch event.
   * Display the fund listing with only favorites funds when switched on.
   * @param updatedSwitchState new switch state when button switched
   */
  public onFavoritesOnlyToggle(updatedSwitchState: SwitchState): void {
    this.ppssService.toggleFavoritesOnlyListing(updatedSwitchState);
  }

  public ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
